"use client";

import Link from "next/link";
import { useCallback, useEffect, useId, useRef, useState } from "react";

import {
  browserNeedsConsentDecision,
  migrateLegacyAnalyticsPreference,
  reconcileConsentWithAccount,
  saveConsentChoices,
  subscribeToConsent,
  subscribeToConsentPreferencesRequests,
  type ConsentSource,
  type SaveConsentOptions,
} from "@/src/features/consent/browserConsent";
import { publishConsentBarReserve } from "@/src/features/consent/consentBarReserve";
import { usePublicFrontendTranslation } from "@smartmanager/shell-ui/shared/i18n/usePublicFrontendTranslation";
import type { ConsentChoices } from "@/src/features/consent/policy";

import {
  CONSENT_SURFACE,
  ConsentPreferencesPanel,
  EQUAL_CHOICE,
  QUIET_LINK,
  readConsentPreferencesStart,
} from "./ConsentPreferences";

/*
 * Tracker consent: for EU/EEA/UK visitors only, a slim bar along the bottom
 * edge on first visit (a compact card on phones, so it never covers a content
 * window). There "Reject all" and "Accept all" are the same size, color and
 * weight, and nothing is pre-ticked: nothing runs until they choose.
 * Customize opens the full choices in place of the bar, never as a modal over
 * the page; `openConsentPreferences()` (the cookie page's button) opens the
 * same panel in the same place. Everyone else gets no bar: analytics is on by
 * default (diagnostics is not) and they turn it off in Settings, Privacy,
 * which shows the same choices inline. The region check and cookie read run
 * after mount, so the server render never depends on them. A browser privacy
 * signal (GPC / Do Not Track) is honored without asking.
 */

export default function ConsentBanner() {
  const [mounted, setMounted] = useState(false);
  const [needsDecision, setNeedsDecision] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [suggestAnalytics, setSuggestAnalytics] = useState(false);
  const t = usePublicFrontendTranslation();
  const bannerTitleId = useId();

  useEffect(() => {
    setMounted(true);
    setSuggestAnalytics(migrateLegacyAnalyticsPreference().suggestAnalytics);
    setNeedsDecision(browserNeedsConsentDecision());
    void reconcileConsentWithAccount();
    const unsubscribeConsent = subscribeToConsent(() => setNeedsDecision(browserNeedsConsentDecision()));
    const unsubscribeRequests = subscribeToConsentPreferencesRequests(() => setCustomizing(true));
    return () => {
      unsubscribeConsent();
      unsubscribeRequests();
    };
  }, []);

  const save = useCallback((choices: ConsentChoices, source: ConsentSource, options?: SaveConsentOptions) => {
    saveConsentChoices(choices, source, options);
    setCustomizing(false);
  }, []);

  // One surface along the bottom edge: the bar, or the full choices in its
  // place. Swapping between them resizes the same element, so the band it
  // reserves follows live and is released only once nothing is shown.
  const visible = mounted && (needsDecision || customizing);
  const surfaceRef = useRef<HTMLDivElement>(null);
  // Cancel goes back to the bar; focus goes back to the Customize that opened the choices.
  const customizeRef = useRef<HTMLButtonElement>(null);
  const refocusCustomize = useRef(false);

  useEffect(() => {
    if (customizing || !refocusCustomize.current) return;
    refocusCustomize.current = false;
    customizeRef.current?.focus();
  }, [customizing]);

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!visible || !surface) return;
    const measure = () => publishConsentBarReserve(window.innerHeight - surface.getBoundingClientRect().top);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(surface);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      publishConsentBarReserve(0);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      ref={surfaceRef}
      data-consent-surface={customizing ? "preferences" : "bar"}
      className={customizing
        ? "fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-[9999] mx-auto max-h-[calc(100dvh-24px)] max-w-[480px] overflow-y-auto"
        : "fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-[9999] mx-auto max-w-[440px] sm:max-w-[1100px]"}
      style={CONSENT_SURFACE}
    >
      {customizing ? (
        <ConsentPreferencesPanel
          className="p-6"
          start={readConsentPreferencesStart(suggestAnalytics)}
          onSave={save}
          onCancel={() => {
            refocusCustomize.current = true;
            setCustomizing(false);
          }}
        />
      ) : (
        <section
          role="region"
          aria-labelledby={bannerTitleId}
          className="px-4 pb-1 pt-3.5 sm:flex sm:flex-wrap sm:items-center sm:gap-x-5 sm:gap-y-2 sm:px-5 sm:py-3 lg:flex-nowrap"
        >
          <div className="min-w-0 sm:basis-full sm:leading-[18px] lg:flex-1 lg:basis-auto">
            <h2 id={bannerTitleId} className="nv-title sm:inline sm:text-[14px] sm:leading-[18px]">{t("Your privacy choices")}</h2>
            <p className="nv-body mt-1 text-nv-dim sm:ml-1.5 sm:mt-0 sm:inline sm:text-[13px] sm:leading-[18px]">
              {t("We use only what is needed to sign you in and keep your account safe. With your OK, we also measure feature use and collect error reports. Nothing is on until you choose.")}
            </p>
          </div>
          <div className="mt-3 flex gap-2 sm:mt-0 sm:grid sm:shrink-0 sm:grid-cols-2">
            <button type="button" className={EQUAL_CHOICE} onClick={() => save({ analytics: false, diagnostics: false }, "banner")}>
              {t("Reject all")}
            </button>
            <button type="button" className={EQUAL_CHOICE} onClick={() => save({ analytics: true, diagnostics: true }, "banner")}>
              {t("Accept all")}
            </button>
          </div>
          <div className="mt-1 flex items-center justify-between sm:mt-0 sm:shrink-0 sm:gap-3">
            <button ref={customizeRef} type="button" className={QUIET_LINK} onClick={() => setCustomizing(true)}>{t("Customize")}</button>
            <Link href="/privacy-policy" className={QUIET_LINK}>{t("Privacy Policy")}</Link>
          </div>
        </section>
      )}
    </div>
  );
}
